/** Export / import for terminal_flow_custom_steps and combined flow config (Phase 8). */

export const CUSTOM_STEPS_EXPORT_VERSION = 1;
export const FLOW_CONFIG_EXPORT_VERSION = 2;

export function buildCustomStepsExport(customSteps = {}, meta = {}) {
  return {
    version: CUSTOM_STEPS_EXPORT_VERSION,
    app: 'mysky-leads-tracker',
    kind: 'custom_steps',
    exported_at: new Date().toISOString(),
    exported_by: meta.exportedBy || null,
    custom_steps: customSteps && typeof customSteps === 'object' ? customSteps : {},
  };
}

export function buildFlowConfigExport(overrides = {}, customSteps = {}, meta = {}) {
  return {
    version: FLOW_CONFIG_EXPORT_VERSION,
    app: 'mysky-leads-tracker',
    kind: 'flow_config',
    exported_at: new Date().toISOString(),
    exported_by: meta.exportedBy || null,
    overrides: overrides && typeof overrides === 'object' ? overrides : {},
    custom_steps: customSteps && typeof customSteps === 'object' ? customSteps : {},
  };
}

function sanitizeCustomStepEntry(entry, flowId) {
  if (!entry || typeof entry !== 'object') return null;
  const handler = entry.handler;
  const stepKey = String(entry.stepKey || '').trim();
  const ask = String(entry.ask || '').trim();
  if (!handler || !stepKey || !ask) return null;
  return {
    id: entry.id || `cs_${flowId}_${stepKey}`,
    flowId,
    handler,
    stepKey,
    ask,
    hint: String(entry.hint || '').trim(),
    choices: String(entry.choices || '').trim(),
    afterStep: String(entry.afterStep || '__end__').trim(),
    optional: entry.optional != null ? !!entry.optional : undefined,
    enabled: entry.enabled !== false,
  };
}

export function parseCustomStepsImport(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return { error: 'Invalid JSON file.' };
    }
  }
  if (!data || typeof data !== 'object') return { error: 'Import must be a JSON object.' };

  let source = data.custom_steps;
  if (!source && data.kind === 'custom_steps' && data.version === CUSTOM_STEPS_EXPORT_VERSION) {
    source = data.custom_steps;
  }
  if (!source && data.version === FLOW_CONFIG_EXPORT_VERSION) {
    source = data.custom_steps;
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return { error: 'Missing "custom_steps" object in import file.' };
  }

  const cleaned = {};
  for (const [flowId, list] of Object.entries(source)) {
    if (!Array.isArray(list)) continue;
    const steps = list.map((s) => sanitizeCustomStepEntry(s, flowId)).filter(Boolean);
    if (steps.length) cleaned[flowId] = steps;
  }
  return { custom_steps: cleaned, meta: data };
}

export function parseFlowConfigImport(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return { error: 'Invalid JSON file.' };
    }
  }
  if (!data || typeof data !== 'object') return { error: 'Import must be a JSON object.' };

  if (data.version === FLOW_CONFIG_EXPORT_VERSION || (data.overrides && data.custom_steps)) {
    const overrides = data.overrides && typeof data.overrides === 'object' ? data.overrides : {};
    const customParsed = parseCustomStepsImport(data);
    if (customParsed.error && !Object.keys(overrides).length) return customParsed;
    return {
      overrides,
      custom_steps: customParsed.custom_steps || {},
      meta: data,
      hasOverrides: Object.keys(overrides).length > 0,
      hasCustomSteps: Object.keys(customParsed.custom_steps || {}).length > 0,
    };
  }

  if (data.overrides || data.version === 1) {
    return {
      overrides: data.overrides || data,
      custom_steps: {},
      meta: data,
      hasOverrides: true,
      hasCustomSteps: false,
    };
  }

  const customOnly = parseCustomStepsImport(data);
  if (customOnly.error) return customOnly;
  return {
    overrides: {},
    custom_steps: customOnly.custom_steps,
    meta: data,
    hasOverrides: false,
    hasCustomSteps: true,
  };
}

export function mergeCustomStepsMaps(base = {}, incoming = {}, mode = 'merge') {
  if (mode === 'replace') return { ...incoming };
  const next = { ...base };
  for (const [flowId, list] of Object.entries(incoming)) {
    const existing = [...(next[flowId] || [])];
    const byKey = new Map(existing.map((s) => [s.stepKey, s]));
    list.forEach((step) => byKey.set(step.stepKey, step));
    next[flowId] = [...byKey.values()];
  }
  return next;
}

export function diffCustomStepsMaps(base = {}, incoming = {}) {
  const added = [];
  const removed = [];
  const changed = [];
  const flowIds = new Set([...Object.keys(base), ...Object.keys(incoming)]);

  flowIds.forEach((flowId) => {
    const bList = Array.isArray(base[flowId]) ? base[flowId] : [];
    const iList = Array.isArray(incoming[flowId]) ? incoming[flowId] : [];
    const bByKey = Object.fromEntries(bList.map((s) => [s.stepKey, s]));
    const iByKey = Object.fromEntries(iList.map((s) => [s.stepKey, s]));
    const keys = new Set([...Object.keys(bByKey), ...Object.keys(iByKey)]);
    keys.forEach((key) => {
      const label = `${flowId}.${key}`;
      if (!bByKey[key] && iByKey[key]) added.push({ key: label, after: iByKey[key] });
      else if (bByKey[key] && !iByKey[key]) removed.push({ key: label, before: bByKey[key] });
      else if (bByKey[key] && iByKey[key] && JSON.stringify(bByKey[key]) !== JSON.stringify(iByKey[key])) {
        changed.push({ key: label, before: bByKey[key], after: iByKey[key] });
      }
    });
  });

  return {
    added,
    removed,
    changed,
    stats: {
      added: added.length,
      removed: removed.length,
      changed: changed.length,
    },
  };
}

export function formatCustomStepsDiffHtml(diff, escFn) {
  const esc = escFn || ((s) => String(s));
  const fmtStep = (step) => {
    if (!step) return '';
    const bits = [
      step.ask ? `ask: ${String(step.ask).slice(0, 60)}${String(step.ask).length > 60 ? '…' : ''}` : '',
      step.hint ? `hint: ${String(step.hint).slice(0, 40)}${String(step.hint).length > 40 ? '…' : ''}` : '',
      step.afterStep ? `after: ${step.afterStep}` : '',
      step.handler ? `handler: ${step.handler}` : '',
    ].filter(Boolean);
    return bits.join(' · ');
  };
  const line = (label, items, fmt) => {
    if (!items.length) return '';
    return `<div style="margin-top:8px"><strong>${esc(label)} (${items.length})</strong><ul style="margin:4px 0 0;padding-left:18px;font-size:11px">${items.map(fmt).join('')}</ul></div>`;
  };
  return [
    line('New custom steps', diff.added, (x) => `<li><code>${esc(x.key)}</code>${fmtStep(x.after) ? `<br><span class="admin-muted">${esc(fmtStep(x.after))}</span>` : ''}</li>`),
    line('Updated custom steps', diff.changed, (x) => `<li><code>${esc(x.key)}</code><br><span class="admin-muted">${esc(fmtStep(x.before))}</span> → <span>${esc(fmtStep(x.after))}</span></li>`),
    line('Removed custom steps', diff.removed, (x) => `<li><code>${esc(x.key)}</code>${fmtStep(x.before) ? `<br><span class="admin-muted">${esc(fmtStep(x.before))}</span>` : ''}</li>`),
  ].filter(Boolean).join('') || '<p class="admin-muted">No custom step changes.</p>';
}

/** Combined import preview summary for flow_config bundle v2. */
export function formatFlowConfigImportSummary(overrideDiff, customDiff, escFn) {
  const esc = escFn || ((s) => String(s));
  const parts = [];
  if (overrideDiff?.stats) {
    parts.push(`Overrides — ${overrideDiff.stats.added} new, ${overrideDiff.stats.changed} updated, ${overrideDiff.stats.removed} removed`);
  }
  if (customDiff?.stats) {
    parts.push(`Custom steps — ${customDiff.stats.added} new, ${customDiff.stats.changed} updated, ${customDiff.stats.removed} removed`);
  }
  if (!parts.length) return '';
  return `<p class="admin-muted" style="margin:0 0 8px">${esc(parts.join(' · '))}</p>`;
}

export function downloadJsonExport(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadCustomStepsJson(customSteps, filename = 'terminal-flow-custom-steps.json') {
  downloadJsonExport(buildCustomStepsExport(customSteps), filename);
}

export function downloadFlowConfigJson(overrides, customSteps, filename = 'terminal-flow-config.json') {
  downloadJsonExport(buildFlowConfigExport(overrides, customSteps), filename);
}
